import {inject, Injectable, Injector} from "@angular/core";
import {Overlay, OverlayConfig, OverlayRef} from "@angular/cdk/overlay";
import {ComponentPortal, ComponentType} from "@angular/cdk/portal";
import {Observable, Subject, Subscription} from "rxjs";
import {ModalOptions} from "../interfaces/ModalOptions.interface";
import {ModalDialogComponent} from "../components/modal-dialog/modal-dialog.component";
import {ModalData} from "../types/modal-data.type";
import {ModalEventType} from "../types/modal.type";
import {ModalClosedEventTypes, ModalEventTypes} from "../enums/modal.event";

@Injectable({
  providedIn: 'root'
})
export default class ModalService {
  private readonly overlay: Overlay = inject(Overlay);

  private modalRef: ModalRef<unknown> | null = null;

  public open(component: ComponentType<unknown>, data: null | object = null, options: ModalOptions = {}): Observable<ModalEventType> {
    const subject: Subject<ModalEventType> = new Subject<ModalEventType>();
    document.body.classList.add('has-modal');

    const overlayRef: OverlayRef = this.overlay.create(
      new OverlayConfig({
        hasBackdrop: true,
        backdropClass: (options.showBackdrop ?? true) ? "backdrop" : "",
        positionStrategy: this.overlay.position().global().top().centerVertically().centerHorizontally(),
        scrollStrategy: this.overlay.scrollStrategies.block(),
        disposeOnNavigation: true,
      })
    );

    this.modalRef = new ModalRef(overlayRef, component, subject, options);
    const injector: Injector = Injector.create({
      providers: [
        {provide: ModalRef, useValue: this.modalRef},
        {provide: ModalData, useValue: data}
      ]
    });

    const portal: ComponentPortal<ModalDialogComponent> = new ComponentPortal<ModalDialogComponent>(ModalDialogComponent, null, injector);

    overlayRef.attach(portal);
    overlayRef.backdropElement?.addEventListener('click', () => {
      if (options.dismissOnBackdropClick === false) {
        return;
      }

      overlayRef.detach();
      document.body.classList.remove('has-modal');
      subject.next({type: ModalEventTypes.Closed});
    });

    const sub: Subscription = subject.asObservable().subscribe((event: ModalEventType) => {
      if (event.type in ModalClosedEventTypes) {
        overlayRef.dispose();
        sub.unsubscribe();
      }
    });

    return subject.asObservable();
  }

  public close(component?: ComponentType<object>): void {
    if (
      component
      && this.modalRef?.getComponent() !== component
    ) {
      return;
    }

    this.modalRef?.close();
    document.body.classList.remove('has-modal');
  }
}

export class ModalRef<T> {
  constructor(
    private readonly overlayRef: OverlayRef,
    private readonly component: ComponentType<T>,
    public readonly subject: Subject<ModalEventType>,
    public readonly options: ModalOptions,
  ) {
  }

  public close(value: object | null = null, type: ModalEventTypes | ModalClosedEventTypes = ModalEventTypes.Closed): void {
    this.overlayRef.detach();
    document.body.classList.remove('has-modal');
    this.subject.next({
      type: type,
      value: value,
    });
  }

  public getComponent(): ComponentType<T> {
    return this.component;
  }

  public cancel(): void {
    this.close(null, ModalEventTypes.Canceled);
  }
}
